import React, { useContext, useEffect, useState } from 'react';
import SubmitButton from '../../components/SubmitButton';
import { axiosKK } from '../../services/networkRequest';
import {
  DiscountAndCount,
  DiscountListResponse,
} from '../../models/APIModels';
import { useHistory } from 'react-router-dom';
import Moment from 'moment';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import { DiscountModal } from '../../components/admin/DiscountModal';
import UserContext from '../../contexts/UserContext';
import { Role } from '../../utilities/Role';
import { ArrowUturnUpIcon, NoSymbolIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { useDiscountFilters } from './useDiscountFilters';
import Switch from 'react-switch';
import { useApi } from '../../services/useApi';
import {
  AddButton,
  DataTable,
  DataTableColumn,
  EmptyState,
  PageHeader,
} from '../../components/admin/Shared';

export default function DiscountComponent(this: any): JSX.Element {
  const history = useHistory();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<boolean | number>(false);
  const [discounts, setDiscounts] = useState<DiscountAndCount[]>([]);
  const [reloadData, setReloadData] = useState(false);
  const { user: loggedInUser } = useContext(UserContext);

  const {
    filterStatus,
    setFilterStatus,
    filterUpFront,
    setFilterUpFront,
    filterRecurring,
    setFilterRecurring,
    filteredDiscounts,
  } = useDiscountFilters(discounts);

  const { data: fetchedDiscounts, loading, error } = useApi(
    () => axiosKK.get<DiscountListResponse>('/discounts').then(({ data }) => data.discounts.filter((d) => !d.deletedAt)),
    [reloadData]
  );

  useEffect(() => {
    if (fetchedDiscounts) setDiscounts(fetchedDiscounts);
  }, [fetchedDiscounts]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [activateId, setActivateId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const columns: DataTableColumn<DiscountAndCount>[] = [
    { key: 'id', label: 'ID', render: (d) => d.id, width: '72px' },
    {
      key: 'code',
      label: 'Code',
      render: (d) => <span className="code">{d.discountCode}</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (d) => {
        const expired = d.expires && Moment(d.expires).isBefore(Moment());
        return expired ? (
          <span className="inactive">inactive</span>
        ) : (
          <span className="active">active</span>
        );
      },
    },
    {
      key: 'percentage',
      label: 'Discount',
      render: (d) => `${d.discountPercentage}%`,
    },
    {
      key: 'upFront',
      label: 'Up-Front',
      render: (d) => (d.upFront ? 'Yes' : 'No'),
    },
    {
      key: 'recurring',
      label: 'Recurring',
      render: (d) => (d.recurring ? 'Yes' : 'No'),
    },
    {
      key: 'count',
      label: 'No. of Uses',
      render: (d) => d.count,
    },
    {
      key: 'expiry',
      label: 'Expiry',
      render: (d) =>
        d.expires ? Moment(d.expires).format('MMM Do YYYY') : 'N/A',
    },
  ];

  return (
    <>
      <PageHeader
        title="Discounts"
        onBack={() => history.push('/admin/dashboard')}
      />

      <section className="admin-all-discounts-info">
        <div className="discount-controls-container">
          <div className="filters-group">
            <div className="filter-item">
              <label>Status</label>
              <select
                className="admin-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
              >
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            <div className="filter-item toggle-item">
              <label>Up-Front</label>
              <Switch
                checked={filterUpFront}
                onChange={setFilterUpFront}
                onColor="#6c3cd9"
                offColor="#dbdbdb"
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={44}
                handleDiameter={18}
              />
            </div>

            <div className="filter-item toggle-item">
              <label>Recurring</label>
              <Switch
                checked={filterRecurring}
                onChange={setFilterRecurring}
                onColor="#6c3cd9"
                offColor="#dbdbdb"
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={44}
                handleDiameter={18}
              />
            </div>
          </div>
        </div>

        {error && filteredDiscounts.length === 0 ? (
          <EmptyState
            variant="error-fallback"
            title="Failed to load discounts"
            body="Please refresh the page to try again."
          />
        ) : (
          <DataTable<DiscountAndCount>
            columns={columns}
            rows={filteredDiscounts}
            keyOf={(d) => d.id}
            loading={loading}
            emptyMessage="No discounts found."
            loadingMessage="Loading discounts…"
            aboveTable={
              loggedInUser?.role === Role.Admin ? (
                <AddButton onClick={() => setShowCreateModal(true)}>
                  Create New
                </AddButton>
              ) : undefined
            }
            actionsColumn={{
              label: 'Actions',
              render: (d) => {
                const expired = d.expires && Moment(d.expires).isBefore(Moment());
                return (
                  <div className="discount-button-wrapper">
                    {loggedInUser?.role === Role.Admin && (
                      <SubmitButton onClick={() => setShowEditModal(d.id)}>
                        <PencilIcon width={16} height={16} /> Edit
                      </SubmitButton>
                    )}
                    {expired ? (
                      <SubmitButton
                        className="activate"
                        onClick={() => setActivateId(d.id)}
                      >
                        <ArrowUturnUpIcon width={16} height={16} /> Reactivate
                      </SubmitButton>
                    ) : (
                      <SubmitButton
                        className="deactivate-btn"
                        onClick={() => setConfirmId(d.id)}
                      >
                        <NoSymbolIcon width={16} height={16} /> Deactivate
                      </SubmitButton>
                    )}
                    {loggedInUser?.role === Role.Admin && (
                      <SubmitButton
                        red={true}
                        className="delete-btn"
                        onClick={() => setDeleteId(d.id)}
                      >
                        <TrashIcon width={16} height={16} /> Delete
                      </SubmitButton>
                    )}
                  </div>
                );
              },
            }}
          />
        )}
      </section>

      {loggedInUser?.role === Role.Admin && (
        <DiscountModal
          showCreate={showCreateModal}
          showEdit={showEditModal}
          close={() => {
            setShowCreateModal(false);
            setShowEditModal(false);
          }}
          discount={discounts.find((value) => value.id === showEditModal)}
          refreshDiscounts={() => setReloadData(!reloadData)}
          updateDiscounts={(discount) => {
            setDiscounts([
              discount,
              ...discounts.filter((value) => value.id !== discount.id),
            ]);
          }}
        />
      )}

      {confirmId !== null && (
        <ConfirmationModal
          id={confirmId}
          show={confirmId !== null}
          close={() => setConfirmId(null)}
          success={() => {
            setConfirmId(null);
            setReloadData(!reloadData);
          }}
          failure={() => setConfirmId(confirmId)}
          directory="/discounts/deactivate/"
          warningMessage="Are you sure you want to deactivate this discount code?"
        />
      )}

      {activateId !== null && (
        <ConfirmationModal
          id={activateId}
          show={activateId !== null}
          close={() => setActivateId(null)}
          success={() => {
            const id = activateId;
            setActivateId(null);
            if (id !== null) setShowEditModal(id);
            setReloadData(!reloadData);
          }}
          failure={() => setActivateId(activateId)}
          directory="/discounts/reactivate/"
          warningMessage="Do you want to reactivate this discount code?"
        />
      )}

      {deleteId !== null && (
        <DeleteConfirmationModal
          show={deleteId !== null}
          close={() => setDeleteId(null)}
          success={() => {
            setDeleteId(null);
            setReloadData(!reloadData);
          }}
          failure={() => setDeleteId(deleteId)}
          directory="/discounts/"
          id={deleteId}
          warningMessage="Are you sure you want to permanently delete this discount code? This cannot be undone."
        />
      )}
    </>
  );
}
